import { useIntl } from "react-intl";

interface usePaymentLocationLabelsProps {
  viewOnly?: boolean;
  selectedLocationReferenceNumber?: string;
}

export default function usePaymentLocationLabels({
  viewOnly,
  selectedLocationReferenceNumber,
}: usePaymentLocationLabelsProps) {
  const intl = useIntl();

  // Helper functions
  const getIntercomOptions = () => [
    {
      value: "off",
      label: intl.formatMessage({
        id: "settings.payment-locations.intercomOff",
        defaultMessage: "Off",
      }),
    },
    {
      value: "on_optional",
      label: intl.formatMessage({
        id: "settings.payment-locations.intercomOnOptional",
        defaultMessage: "On, optional",
      }),
    },
    {
      value: "on_mandatory",
      label: intl.formatMessage({
        id: "settings.payment-locations.intercomOnMandatory",
        defaultMessage: "On, mandatory",
      }),
    },
    {
      value: "company_settings",
      label: intl.formatMessage({
        id: "settings.payment-locations.intercomCompanySettings",
        defaultMessage: "Company settings",
      }),
    },
  ];

  const getDefaultIncotermOptions = () => [
    {
      value: "None",
      label: intl.formatMessage({
        id: "settings.payment-locations.none",
        defaultMessage: "None",
      }),
    },
    {
      value: "EXW",
      label: intl.formatMessage({
        id: "settings.payment-locations.exWorks",
        defaultMessage: "EXW - Ex Works",
      }),
    },
    {
      value: "FCA",
      label: intl.formatMessage({
        id: "settings.payment-locations.freeCarrier",
        defaultMessage: "FCA - Free Carrier",
      }),
    },
    {
      value: "CPT",
      label: intl.formatMessage({
        id: "settings.payment-locations.carriagePaidTo",
        defaultMessage: "CPT - Carriage Paid To",
      }),
    },
    {
      value: "CIP",
      label: intl.formatMessage({
        id: "settings.payment-locations.carriageInsurancePaidTo",
        defaultMessage: "CIP - Carriage Insurance Paid To",
      }),
    },
    {
      value: "DAT",
      label: intl.formatMessage({
        id: "settings.payment-locations.deliveryAtTerminal",
        defaultMessage: "DAT - Delivery at Terminal",
      }),
    },
    {
      value: "DAP",
      label: intl.formatMessage({
        id: "settings.payment-locations.deliveryAtPlace",
        defaultMessage: "DAP - Delivery at Place",
      }),
    },
    {
      value: "DDP",
      label: intl.formatMessage({
        id: "settings.payment-locations.deliveryDutyPaid",
        defaultMessage: "DDP - Delivery Duty Paid",
      }),
    },
    {
      value: "FAS",
      label: intl.formatMessage({
        id: "settings.payment-locations.freeAlongsideShip",
        defaultMessage: "FAS - Free Alongside Ship",
      }),
    },
    {
      value: "FOB",
      label: intl.formatMessage({
        id: "settings.payment-locations.freeOnBoard",
        defaultMessage: "FOB - Free on Board",
      }),
    },
    {
      value: "CFR",
      label: intl.formatMessage({
        id: "settings.payment-locations.costAndFreight",
        defaultMessage: "CFR - Cost and Freight",
      }),
    },
    {
      value: "CIF",
      label: intl.formatMessage({
        id: "settings.payment-locations.costInsuranceFreight",
        defaultMessage: "CIF - Cost, Insurance and Freight",
      }),
    },
    {
      value: "DPU",
      label: intl.formatMessage({
        id: "settings.payment-locations.deliveredAtPlaceUnloaded",
        defaultMessage: "DPU - Delivered at Place Unloaded",
      }),
    },
  ];

  const getDialogTitle = () => {
    if (viewOnly) {
      return "View payment location";
    }
    if (selectedLocationReferenceNumber) {
      return intl.formatMessage({
        id: "settings.payment-locations.editPaymentLocation",
        defaultMessage: "Edit payment location",
      });
    }
    return intl.formatMessage({
      id: "settings.payment-locations.addPaymentLocation",
      defaultMessage: "Add payment location",
    });
  };

  return { getIntercomOptions, getDefaultIncotermOptions, getDialogTitle };
}
